/**
 * BugReportWidget — sağ altta sabit "Sorun Bildir" butonu + modal.
 *
 * Açıklama zorunlu, ekran görüntüsü opsiyonel; bulunulan sayfa URL'i otomatik
 * eklenir. Admin panelde gizlenir (orada ayrı inceleme sayfası var).
 */
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Bug, X, Image as ImageIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { getApiErrorMessage } from '../utils/apiError';
import { useSubmitBugReport } from '../api/bugReports';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';

export default function BugReportWidget() {
  const { lang } = useLanguage();
  const { user } = useAuth();
  const { pathname, search } = useLocation();
  const toast = useToast();
  const submit = useSubmitBugReport();
  const T = (tr, en) => (lang === 'tr' ? tr : en);

  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [screenshot, setScreenshot] = useState(null);

  // Yalnızca giriş yapmış kullanıcıya, admin sayfaları dışında göster
  if (!user || pathname.startsWith('/admin')) return null;

  const pageUrl = `${pathname}${search || ''}`;

  const reset = () => { setMessage(''); setScreenshot(null); };

  const handleSubmit = async () => {
    if (!message.trim()) {
      toast.error(T('Lütfen sorunu açıklayın', 'Please describe the issue'));
      return;
    }
    try {
      await submit.mutateAsync({ message: message.trim(), pageUrl, screenshot });
      toast.success(T('Bildiriminiz alındı, teşekkürler!', 'Report received, thank you!'));
      reset();
      setOpen(false);
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (f && f.size > 5 * 1024 * 1024) {
      toast.error(T('Dosya çok büyük (maks 5MB)', 'File too large (max 5MB)'));
      return;
    }
    setScreenshot(f || null);
  };

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-5 right-5 z-40 flex items-center gap-2 px-4 py-3 bg-[var(--brand-primary)] text-white font-bold text-sm rounded-full shadow-lg hover:bg-[var(--brand-primary-hover)] transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)]"
        aria-label={T('Sorun Bildir', 'Report a problem')}
        data-testid="bug-report-button"
      >
        <Bug size={18} aria-hidden="true" />
        <span className="hidden sm:inline">{T('Sorun Bildir', 'Report a problem')}</span>
      </button>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{T('Sorun Bildir', 'Report a Problem')}</DialogTitle>
          </DialogHeader>

          <div className="flex flex-col gap-4 py-2">
            <div className="text-xs text-[var(--text-muted)] bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
              {T('Sayfa', 'Page')}: <span className="font-mono">{pageUrl}</span>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-gray-600">
                {T('Sorunu açıklayın', 'Describe the issue')} *
              </Label>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
                placeholder={T('Ne oldu? Hangi adımdan sonra?', 'What happened? After which step?')}
                data-testid="bug-report-message"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-gray-600 flex items-center gap-1">
                <ImageIcon size={14} /> {T('Ekran görüntüsü (opsiyonel)', 'Screenshot (optional)')}
              </Label>
              <input
                type="file"
                accept="image/*"
                onChange={onFile}
                className="block w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-sky-50 file:text-sky-700 hover:file:bg-sky-100"
                data-testid="bug-report-screenshot"
              />
              {screenshot && (
                <div className="flex items-center gap-2 text-xs text-emerald-600">
                  <span className="truncate flex-1">{screenshot.name}</span>
                  <button onClick={() => setScreenshot(null)} aria-label="remove"><X size={14} /></button>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setOpen(false); reset(); }}>
              {T('İptal', 'Cancel')}
            </Button>
            <Button onClick={handleSubmit} disabled={submit.isPending}>
              {submit.isPending ? T('Gönderiliyor…', 'Sending…') : T('Gönder', 'Send')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
